/**
 * Which "generation" of a brand's contacts the browser's read cache may
 * serve (`lib/audience/contacts-page-cache.ts`). A contact write the app
 * makes moves the brand to a new generation: no page read before it is
 * served again, and a read still in flight from before it is neither joined
 * nor stored.
 *
 * Kept apart from the cache itself on purpose: every write site (a grid edit,
 * a contact sheet save, a manual add, a delete, a finished import or
 * validation run) imports this, while the cache and the fetch code load only
 * when the grid first reads (`/audience` is at its client bundle budget). The
 * app shell imports it too, to forget the reads when the session's owner
 * changes ({@link forgetAudienceContactsReads}); it must import nothing, so
 * the shell never carries the cache.
 */

const brandGenerations = new Map<string, number>()

/** Each loaded read cache's clear, handed over by the cache module. */
const readCacheClears = new Set<() => void>()

export function getAudienceContactsGeneration(brandId: string): number {
  return brandGenerations.get(brandId) ?? 0
}

/** The app wrote this brand's contacts: never serve a page read before now. */
export function invalidateAudienceContacts(
  brandId: string | null | undefined
): void {
  if (brandId) {
    brandGenerations.set(brandId, getAudienceContactsGeneration(brandId) + 1)
  }
}

/** Forget every generation (tests; the cache's `clearAudienceContactsCache`). */
export function resetAudienceContactsGenerations(): void {
  brandGenerations.clear()
}

/**
 * The read cache registers its clear here when it loads; the returned
 * function takes it back (tests).
 */
export function registerAudienceContactsCacheClear(
  clear: () => void
): () => void {
  readCacheClears.add(clear)
  return () => {
    readCacheClears.delete(clear)
  }
}

/**
 * Forget every contacts read this tab cached, and cancel the ones in flight:
 * the person signed out or changed, or the organization or brand did (the
 * account menu, `OrgSwitchSync`, the brand switch). Rows and counts read for
 * one session must never be served to the next. Before the grid's first read
 * the cache has not loaded and there is nothing to forget, so this never
 * loads it.
 */
export function forgetAudienceContactsReads(): void {
  resetAudienceContactsGenerations()
  for (const clear of readCacheClears) {
    clear()
  }
}
